import React, { useEffect, useRef, useState } from "react";

import clsx from "clsx";
import { motion, type PanInfo } from "framer-motion";

import { useUser } from "@components/user-context";
import { prettyLogEvent } from "@internal/analytics";
import { Pages } from "@north-beam/nb-client-side/constants";

function Border() {
  return (
    <div className={clsx("flex-1 pointer-events-none transition-colors")} />
  );
}

function graphSizeConstaint(innerHeight: number, baseHeight: number) {
  return innerHeight - 56 - 29 - baseHeight;
}

export function ResizableComponent({
  height,
  children,
}: {
  height: number;
  children: ({ currentHeight }: { currentHeight: number }) => JSX.Element;
}) {
  const { user } = useUser();
  const [resizeY, setResizeY] = useState(0);
  const [maxHeight, setMaxHeight] = useState(
    graphSizeConstaint(window.innerHeight, height),
  );
  const constraintsRef = useRef(null);

  const handleDrag = (
    e: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    const newResizeY = resizeY - info.delta.y;

    const heightConstraint = graphSizeConstaint(window.innerHeight, height);
    if (newResizeY >= 0) {
      setResizeY(newResizeY > heightConstraint ? heightConstraint : newResizeY);
    }
  };

  const currentHeight = height + resizeY;

  useEffect(() => {
    const updateAvailableHeight = () => {
      const maxHeight = graphSizeConstaint(window.innerHeight, height);
      setMaxHeight(maxHeight);
      if (currentHeight > maxHeight) {
        setResizeY(maxHeight);
      }
    };

    window.addEventListener("resize", updateAvailableHeight);

    return () => window.removeEventListener("resize", updateAvailableHeight);
  }, [currentHeight, height]);

  return (
    <motion.div
      className="flex flex-col absolute bottom-0 w-full z-10"
      ref={constraintsRef}
      style={{ height: currentHeight }}
    >
      <div className="relative w-full flex">
        <motion.div
          className={clsx(
            "rounded-t-[20px] text-center w-[12rem] h-[1.8rem] flex cursor-pointer",
            "absolute -top-[1.52rem] right-1/2 translate-x-1/2 transform ",
            "transition-colors items-center justify-center",
            "bg-primary",
          )}
          style={{
            boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.25)",
          }}
          dragMomentum={false}
          drag="y"
          dragElastic={false}
          dragConstraints={{ top: 0, bottom: 0 }}
          onDrag={handleDrag}
          onDoubleClick={() => {
            prettyLogEvent.onDoubleClick({
              user,
              page: Pages.CREATIVE_ANALYTICS,
              domain: "Graph Tab",
            });
            if (currentHeight > height) {
              setResizeY(0);
            } else {
              setResizeY(maxHeight);
            }
          }}
        >
          <div className="border-t-4 rounded-md border-[#ECEAEA] w-[3rem]" />
        </motion.div>
        <Border />
        <div className="w-[14rem]" />
        <Border />
      </div>
      <div className={clsx("grow w-full h-full -mb-[0.575rem]")}>
        {children({ currentHeight })}
      </div>
    </motion.div>
  );
}
